import { Box, Button, Dialog, DialogTitle } from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AllListDialogContentComponentFactory } from '~/components/MyWorkspace/ListWidget';
import { WithPerspectives } from '~/components/MyWorkspace/ManagePerspectives';
import { Widget } from '~/components/MyWorkspace/Widget';
import { useVisible } from '~/hooks/useVisible';
import { useWorkspaceSettings } from '~/scenes/MyWorkspace/hooks';
import { People } from '~/services/Http';
import { useGetTeamAnniversaries } from '~/services/Http/hooks';

import { WidgetProps } from '../../data/widgets';

import { Content } from './components/Content';
import { DialogAnniversary } from './components/DialogAnniversary';
import { useSxStyles } from './useStyles';

export interface Props extends WidgetProps<WithPerspectives> {}

const AllTeamAnniversariesDialogContent = AllListDialogContentComponentFactory({
  ListItemComponent: DialogAnniversary,
  getIdentifier: (item: People.AnniversaryDto) =>
    `${item.employee.id}-${item.type}`,
});

export const TeamAnniversariesWidget: React.FC<Props> = ({ title, width }) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  // TODO activeWorkspaceRole should be passed through as part of perspective
  const { activeWorkspaceRole } = useWorkspaceSettings();

  const getTeamAnniversariesReqParams = {
    employeeId: 'me',
    roles: [activeWorkspaceRole],
  };
  const {
    data: teamAnniversaries = [],
    loading,
    failedToLoad,
  } = useGetTeamAnniversaries(getTeamAnniversariesReqParams);

  const {
    isVisible: isAllTeamAnniversariesDialogOpen,
    show: showAllTeamAnniversaries,
    hide: hideAllTeamAnniversariesDialog,
  } = useVisible();

  const filteredTeamAnniversaries = teamAnniversaries.filter(
    (anniversary) => !!anniversary.employee,
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { height } = useComponentSize(containerRef);

  const noOfFittingColumns = Math.floor(width / 118);
  const noOfFittingRows = Math.floor(height / 120);

  return (
    <>
      <Widget
        title={title || t('teamAnniversaries')}
        content={
          <Box ref={containerRef} sx={sxStyles.refDiv}>
            <Content
              teamAnniversariesLoading={loading}
              teamAnniversaries={filteredTeamAnniversaries}
              failedToLoadTeamAnniversaries={failedToLoad}
              containerWidth={width}
              containerHeight={height}
              noOfFittingColumns={noOfFittingColumns}
              noOfFittingRows={noOfFittingRows}
            />
          </Box>
        }
        actions={
          <Button
            size="small"
            color="primary"
            disabled={
              filteredTeamAnniversaries.length <=
              noOfFittingColumns * noOfFittingRows
            }
            onClick={showAllTeamAnniversaries}
            data-cy="show-all"
          >
            {t('showAll')}
          </Button>
        }
        dataCy="team-anniversaries-widget"
      />
      <Dialog
        open={isAllTeamAnniversariesDialogOpen}
        onClose={hideAllTeamAnniversariesDialog}
        data-cy="all-list-widget-dialog"
      >
        <DialogTitle>{t('teamAnniversaries')}</DialogTitle>
        <AllTeamAnniversariesDialogContent
          listItems={filteredTeamAnniversaries}
        />
      </Dialog>
    </>
  );
};
